import { useEffect, useMemo, useState } from "react";
import { CheckCircleOutline } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import Button from "@components/Button/Button";
import FormItem from "@components/FormItem/FormItem";
import Subheader from "@components/Subheader/Subheader";
import { useNotification } from "@context/NotificationContext";
import PageLayout from "@layouts/PageLayout";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import {
  checkVesselDeficiencies,
  getVesselNominationHeader,
  QUERY_KEY,
  scanNomination,
} from "@services/vesselNominationApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runInquiry } from "@telemetry/runInquiry";
import { paths } from "@utils/navigation";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { usePreNominationRequestFormStore } from "../pre-nomination-request/store/usePreNominationRequestForm.store";
import ErrorMessage from "./ErrorMessage";
import {
  canRequestVesselNomination,
  getNominationTypeOptions,
  NOMINATION_TYPE,
} from "./nominationTypeOptions";
import { getPostScanTarget } from "./postScanNavigation";
import { imoOrNameSchema, vesselNominationScanSchema } from "./VesselNominationScanSchema";

type NominationVesselScanFormValues = {
  imoOrName: string;
  nominationType: string;
};

const LANG_BASE = "vessel-nomination.vessel-scan";

const TELEMETRY_SHARED_MODULE = "vessel_nomination";
const TELEMETRY_DRY_BULK_MODULE = "dry_bulk_vessel_nomination";

const PAGE_NUMBER = 0;
const PAGE_SIZE = 25;

const VesselNominationScanPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const { notify } = useNotification();
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const { setField } = usePreNominationRequestFormStore();
  const { userActionPermissions } = useAuthStore();
  const nominationTypeOptions = useMemo(
    () => getNominationTypeOptions(userActionPermissions, t),
    [userActionPermissions, t],
  );
  const defaultNominationType =
    nominationTypeOptions.length === 1 ? nominationTypeOptions[0].value : "";
  const methods = useForm<{ imoOrName: string; nominationType: string }>({
    defaultValues: { imoOrName: "", nominationType: defaultNominationType },
  });
  const { setError, clearErrors } = methods;
  const [imoOrName, setImoOrName] = useState("");
  const [selectedNominationType, setSelectedNominationType] = useState(defaultNominationType);
  const [notFoundQuery, setNotFoundQuery] = useState(false);
  const [hasDeficiency, setHasDeficiency] = useState(false);

  useEffect(() => {
    if (!canRequestVesselNomination(userActionPermissions)) {
      navigate(`/${paths.vesselNomination.index}`);
    }
  }, [userActionPermissions, navigate]);

  const mutation = useMutation({
    mutationFn: ({ imoOrName, nominationType }: { imoOrName: string; nominationType: string }) =>
      runInquiry(
        {
          module:
            nominationType === NOMINATION_TYPE.DRY_BULK
              ? TELEMETRY_DRY_BULK_MODULE
              : TELEMETRY_SHARED_MODULE,
          screen: "VesselNominationScan",
          action: "scan_vessel",
          entityType: "vessel",
        },
        () =>
          scanNomination({
            imoOrName,
            nominationType,
            pageNumber: PAGE_NUMBER,
            pageSize: PAGE_SIZE,
          }),
      ),
    onSuccess: async (payload) => {
      setNotFoundQuery(false);
      setHasDeficiency(false);
      const vessels = payload?.vessels || [];
      if (vessels.length === 1) {
        let deficiencyCleared = false;
        const deficiencyChain = checkVesselDeficiencies(vessels[0].id)
          .then(() => {
            deficiencyCleared = true;
          })
          .catch((error) => {
            if (error.response?.status === 409) {
              setHasDeficiency(true);
              return;
            }
            notify({
              title: t(`${LANG_BASE}.scan-error-title`),
              description:
                (error.response?.data &&
                typeof error.response.data === "object" &&
                "detail" in error.response.data
                  ? (error.response.data as { detail?: string }).detail
                  : undefined) || t(`${LANG_BASE}.scan-error-description`),
              type: "error",
            });
            console.error("Vessel scan deficiency error:", error);
          });
        const headerReady =
          selectedNominationType === NOMINATION_TYPE.DRY_BULK
            ? queryClient.prefetchQuery({
                queryKey: [QUERY_KEY, "vessel-header", vessels[0].id],
                queryFn: () => getVesselNominationHeader(vessels[0].id),
                retry: false,
              })
            : Promise.resolve();
        await Promise.all([deficiencyChain, headerReady]);
        if (!deficiencyCleared) return;
        notify({
          title: t(`${LANG_BASE}.success`),
          type: "success",
        });
        if (selectedNominationType !== NOMINATION_TYPE.DRY_BULK) {
          setField("vesselId", vessels[0].id);
        }
        const target = getPostScanTarget(selectedNominationType, vessels[0]);
        navigate(target.path, { state: target.state });
        return;
      }
      if (vessels.length > 1) {
        navigate("/vessel-nomination/scan-results", {
          state: {
            partialName: imoOrName.trim(),
            nominationType: selectedNominationType,
            vessels,
            pagination: {
              total: payload.totalRecordCount,
              pages: payload.totalPages,
              pageSize: payload.pageSize,
              pageNumber: payload.pageNumber,
            },
          },
        });
        return;
      }
      setNotFoundQuery(true);
    },
    onError: (error: any) => {
      const statusCode = error.response?.status;
      const backendMessage = error.response?.data?.detail as string | undefined;

      if (statusCode === 400 && backendMessage?.includes(t(`${LANG_BASE}.not-registered-title`))) {
        setNotFoundQuery(true);
        return;
      }

      notify({
        title: t(`${LANG_BASE}.scan-error-title`),
        description:
          (error.response?.data &&
          typeof error.response.data === "object" &&
          "detail" in error.response.data
            ? (error.response.data as { detail?: string }).detail
            : undefined) || t(`${LANG_BASE}.scan-error-description`),
        type: "error",
      });
      console.error("Vessel scan error:", error);
    },
  });

  const reset = () => {
    setImoOrName("");
    setSelectedNominationType(defaultNominationType);
    methods.clearErrors();
    methods.setValue("imoOrName", "");
    methods.setValue("nominationType", defaultNominationType);
  };

  const handleImoOrNameChange = (value: string) => {
    setImoOrName(value);

    const label = t(`${LANG_BASE}.imo-or-vessel`);
    const isDigitsOnly = /^\d+$/.test(value.trim());
    const parsed = imoOrNameSchema(t, label, isDigitsOnly).safeParse(value.trim());

    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setError("imoOrName", {
        type: "manual",
        message: issue?.message || "form-validation.invalid",
      });
    } else {
      clearErrors("imoOrName");
    }
  };

  const validateForm = (formValues: NominationVesselScanFormValues) => {
    const isDigitsOnly = /^\d+$/.test(formValues.imoOrName.trim());
    const schema = vesselNominationScanSchema(
      t,
      t(`${LANG_BASE}.imo-or-vessel`),
      t(`${LANG_BASE}.type`),
      isDigitsOnly,
    );

    const parsed = schema.safeParse({
      imoOrName: formValues.imoOrName.trim(),
      nominationType: formValues.nominationType,
    });

    if (!parsed.success) {
      parsed.error.issues.forEach((issue) => {
        const fieldName = issue.path[0] as keyof NominationVesselScanFormValues;
        methods.setError(fieldName, {
          type: "manual",
          message: issue.message,
        });
      });
      return false;
    }

    methods.clearErrors();
    return true;
  };

  const handleScan = () => {
    if (mutation.isPending) return;
    if (!validateForm({ imoOrName: imoOrName, nominationType: selectedNominationType })) return;
    mutation.mutate({ imoOrName: imoOrName.trim(), nominationType: selectedNominationType });
  };

  if (notFoundQuery) {
    return (
      <ErrorMessage
        errorMessageTitle={t(`${LANG_BASE}.not-registered-title`)}
        errorMessageDescription={t(`${LANG_BASE}.not-registered-description`)}
        cancelButtonLabel={t(`${LANG_BASE}.scan-again`)}
        confirmButtonLabel={t(`${LANG_BASE}.register-vessel`)}
        onCancel={() => {
          setNotFoundQuery(false);
          reset();
        }}
        onConfirm={() => navigate("/vessel-management/register/vessel-information")}
        testId="vessel-scan-not-registered"
      />
    );
  }

  if (hasDeficiency) {
    return (
      <ErrorMessage
        errorMessageTitle={t(`${LANG_BASE}.has-deficiency-title`)}
        errorMessageDescription={t(`${LANG_BASE}.has-deficiency-description`)}
        confirmButtonLabel={t(`${LANG_BASE}.view-deficiencies`)}
        onCancel={() => {
          setHasDeficiency(false);
          reset();
        }}
        onConfirm={() => navigate(`/${paths.deficiencies.index}`)}
      />
    );
  }

  return (
    <PageLayout dir={dir}>
      <Subheader
        title={t("vessel-nomination.create-vessel-nomination")}
        subtitle={
          <span>
            <button
              onClick={() => navigate(`/${paths.vesselNomination.index}`)}
              className="cursor-pointer bg-transparent border-0 p-0"
              style={{ color: theme.palette.text.brand }}
            >
              {t("navigation.vessel-nomination")}
            </button>{" "}
            / {t("vessel-nomination.create-vessel-nomination")}
          </span>
        }
      />
      <FormProvider {...methods}>
        <div className="flex justify-center mt-12">
          <div
            className="w-[640px] rounded-lg overflow-hidden"
            style={{
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
            }}
            data-testid="vessel-scan-card"
          >
            <div className="py-3 px-5" style={{ background: theme.palette.grey[100] }}>
              <span
                className="text-sm font-extrabold"
                style={{ color: theme.palette.text.primary }}
              >
                {t(`${LANG_BASE}.title`)}
              </span>
            </div>
            <div className="px-5 pt-6 pb-5">
              <FormItem
                name="imoOrName"
                type="text"
                label={t(`${LANG_BASE}.imo-or-vessel`)}
                testId="vessel-scan-input-query"
                value={imoOrName}
                onFieldChange={(_, value) => handleImoOrNameChange(value as string)}
                enforceUppercase
              />
              <FormItem
                name="nominationType"
                type="select"
                options={nominationTypeOptions}
                label={t(`${LANG_BASE}.type`)}
                testId="vessel-scan-input-select"
                value={selectedNominationType}
                onFieldChange={(_, value) => setSelectedNominationType(value as string)}
              />
              <div className="flex justify-end gap-4 mt-5">
                <Button
                  variant="text"
                  color="secondary"
                  label={t("common.cancel")}
                  onClick={() => navigate(`/${paths.vesselNomination.index}`)}
                  testId="vessel-scan-cancel-button"
                  sx={{ textTransform: "none" }}
                />
                <Button
                  variant="contained"
                  onClick={handleScan}
                  testId="vessel-scan-scan-button"
                  sx={{
                    textTransform: "none",
                    minWidth: 108,
                    display: "flex",
                    justifyContent: "center",
                  }}
                  startIcon={
                    !mutation.isPending ? <CheckCircleOutline fontSize="small" /> : undefined
                  }
                  loading={mutation.isPending}
                  label={t(`${LANG_BASE}.verify`)}
                />
              </div>
            </div>
          </div>
        </div>
      </FormProvider>
    </PageLayout>
  );
};

export default VesselNominationScanPage;
